import { has } from 'lodash';
import { getDefaultHeaders, http } from '../../../utils/apiRequests';
import { ServiceProvider } from '../ProviderService';

export interface Extension {
  category?: string;
  displayName: string;
  features?: {
    [key: string]: boolean;
  };
  isDeprecated?: boolean;
  logoName?: string;
  name: string;
  objectUUID: string;
}

export interface Module {
  transits?: {} | null;
  params?: any;
  features?: {
    [key: string]: boolean;
  };
  id: string;
  isDeprecated?: boolean;
  name: string;
  objectUUID: string;
  pluginUUID: string;
  description: string;
  isSystem?: boolean;
  exits?: string[];
}

export interface ExtensionRegistrationData {
  [key: string]: {
    pluginUUID: string;
    defaultUserUUID: string;
    registrationData: any;
    charge: number;
    registeredTime: number;
    registrationDone: boolean;
    oauthDone: boolean;
  };
}

export async function getExtensionRegistrationData(pluginId?: string) {
  const url = `/api/plugin/${pluginId ? pluginId : 'all'}/provider`;

  const config = {
    method: 'get',
    headers: getDefaultHeaders(),
  };

  const response = await http(url, config);
  const json = await response.json();

  return json;
}

export function isExtensionRegistered(
  registeredExtensionsMap: ExtensionRegistrationData,
  plugin: Extension
): boolean {
  return has(registeredExtensionsMap, plugin.objectUUID);
}

export const getAllExtensions = async (
  isPublic: boolean,
  fromRecord = 0,
  httpFunc: (request: RequestInfo, config: any) => any = http
): Promise<[]> => {
  const query = {
    filters: [
      {
        field: 'isPublic',
        condition: 'IS_EQUAL_TO',
        value: [isPublic],
      },
    ],
    orderBy: [
      {
        field: 'createTime',
        ascending: true,
      },
    ],
    fromRecord,
    maxRecords: 200,
  };

  const url = `/api/query/plugin?pagination=1`;

  const config = {
    method: 'post',
    body: JSON.stringify(query),
    headers: getDefaultHeaders(),
  };

  const response = await httpFunc(url, config);
  const json = await response.json();
  const data = json.data;

  if (json.info.to < json.info.total - 1) {
    return data.concat(await getAllExtensions(isPublic, json.info.to + 1));
  } else {
    return data;
  }
};

export const getAllModules = async (
  isPublic: boolean,
  fromRecord = 0,
  httpFunc: (request: RequestInfo, config: any) => any = http
): Promise<Module[]> => {
  const query = {
    filters: [
      {
        field: 'isPublic',
        condition: 'IS_EQUAL_TO',
        value: [isPublic],
      },
    ],
    orderBy: [
      {
        field: 'createTime',
        ascending: true,
      },
    ],
    fromRecord,
    maxRecords: 200,
  };

  const url = `/api/query/module?pagination=1`;

  const config = {
    method: 'post',
    body: JSON.stringify(query),
    headers: getDefaultHeaders(),
  };

  const response = await httpFunc(url, config);
  const json = await response.json();
  const data = json.data;

  const uniqueModules = getUniqueModules(data);

  if (json.info.to < json.info.total - 1) {
    return uniqueModules.concat(
      await getAllModules(isPublic, json.info.to + 1)
    );
  } else {
    return uniqueModules;
  }
};

export const getUniqueModules = (data: Module[]): Module[] => {
  const moduleSet = new Set();
  return data.filter(
    (module: Module) => !moduleSet.has(module.id) && moduleSet.add(module.id)
  );
};

export const isExtensionVisible = (
  extension: Extension,
  providerFeatures: ServiceProvider['features'],
  registeredExtensionsMap: ExtensionRegistrationData
) => {
  if (isExtensionRegistered(registeredExtensionsMap, extension)) {
    return true;
  }

  // If the extension is deprecated, we will not show it.
  if (extension.isDeprecated) {
    return false;
  }

  // Return if a user-defined plugin
  if (extension.category === 'User') {
    return true;
  }

  // Return if plugin.features is set with the public tag
  if (extension.features && extension.features['public']) {
    return true;
  }

  if (extension.features) {
    let include = false;

    Object.keys(extension.features).forEach(feature => {
      if (
        providerFeatures &&
        providerFeatures.plugins &&
        providerFeatures.plugins[feature] === true
      ) {
        include = true;
      }
    });

    if (
      !include &&
      providerFeatures &&
      providerFeatures.plugins &&
      providerFeatures.plugins[extension.name] === true
    ) {
      include = true;
    }

    return include;
  }

  return false;
};

export const isModuleVisible = (
  module: Module,
  extension: Extension,
  providerFeatures: ServiceProvider['features']
) => {
  if (module.isDeprecated === true) {
    return false;
  }

  if (module.features || extension.features) {
    // Assumption: if the module has a feature flag set and the provider doesn't,
    if (module.features && !providerFeatures) {
      return false;
    }

    // Return if module.features is set with the public tag
    if (module.features && module.features['public']) {
      return true;
    }

    // If the module features & extension features match
    if (
      module.features &&
      extension !== undefined &&
      extension.features !== undefined
    ) {
      const compareMaps = (
        map1: { [x: string]: any },
        map2: { [x: string]: any }
      ) => {
        let result = true;

        Object.keys(map1).forEach(key => {
          if (map1[key] !== map2[key]) {
            result = false;
          }
        });

        return result;
      };

      if (compareMaps(extension.features, module.features)) {
        return true;
      }
    }

    if (module.features && providerFeatures) {
      let include = false;
      Object.keys(module.features).forEach(feature => {
        if (
          providerFeatures &&
          providerFeatures.plugins &&
          providerFeatures.plugins[feature]
        ) {
          include = true;
        }
      });
      return include;
    }

    if (extension && extension.features && extension.features['public']) {
      return true;
    }
  }

  return false;
};
